import React, { useCallback, useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Activity, CalendarCheck2, ChevronRight, Clock3, Database, ExternalLink, FileText, FolderKanban, GitCompare, Info, LayoutDashboard, Link2, LogOut, Menu, MoreHorizontal, Package, PanelLeftClose, PanelLeftOpen, RotateCcw, Settings, ShieldAlert, Sparkles, Timer, Truck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { useSession } from '@/state/session';
import { useData } from '@/state/data';
import { useDemoStore } from '@/state/store';
import { clearDemoStorage, readJson, writeJson } from '@/state/storage';
import { roleLabel } from '@/domain/roles';
import { formatDateTime } from '@/domain/time';
import { DEMO_NOW_UTC } from '@/fixtures/scenarios';
import { FilterMobileBar, FilterToolbar } from './FilterBar';
import { AboutDialog } from './AboutDialog';
import { cn } from '@/lib/utils';
import { BrandLogo } from '@/components/brand/BrandLogo';
import { DRIFT_REPORTS, ADMIN_SECTIONS } from '@/lib/driftApi';

/*
  Hovedmeny (Portal 2026): arbeidsorientert rekkefølge med små gruppeoverskrifter.
  Oversikt står øverst som arbeidsstart. Registrering = det daglige (Min dag, Timer, Vareforbruk, Prosjekter),
  Drift = maskiner og produksjon, Kvalitet = HMS/RUH/kvalitetsavvik, Innsikt = rapporter, datakilder og KI-assistent.
  Innstillinger (grunnregistrene) ligger alltid nederst. Ingen nye moduler – kun eksisterende ruter.
*/
export const NAV_GROUPS = [
  {
    id: 'start',
    label: null,
    items: [{ to: '/oversikt', label: 'Oversikt', icon: LayoutDashboard }],
  },
  {
    id: 'registrering',
    label: 'Registrering',
    items: [
      { to: '/min-dag', label: 'Min dag', icon: CalendarCheck2 },
      { to: '/timer', label: 'Timer', icon: Timer },
      { to: '/vareforbruk', label: 'Vareforbruk', icon: Package },
      { to: '/prosjekter', label: 'Prosjekter', icon: FolderKanban },
    ],
  },
  {
    id: 'drift',
    label: 'Drift',
    items: [
      { to: '/maskiner', label: 'Maskiner', icon: Truck },
      { to: '/produksjon', label: 'Produksjon', icon: Activity },
      { to: '/sammenlign', label: 'Sammenlign', icon: GitCompare },
    ],
  },
  {
    id: 'kvalitet',
    label: 'Kvalitet',
    items: [{ to: '/avvik', label: 'Avvik', icon: ShieldAlert }],
  },
  {
    id: 'innsikt',
    label: 'Innsikt',
    items: [
      { to: '/rapporter', label: 'Rapporter', icon: FileText },
      { to: '/datakilder', label: 'Datakilder', icon: Database },
      { to: '/assistent', label: 'KI-assistent', icon: Sparkles, accent: true },
    ],
  },
];

export const NAV = NAV_GROUPS.flatMap((g) => g.items);

/** Innstillinger ligger som ett tydelig punkt nederst i menyen (grunnregistrene som driver registreringene). */
export const SETTINGS_ITEM = { to: '/innstillinger', label: 'Innstillinger', icon: Settings };

/** Bredde på innholdsflaten – flytende, med rolig maksbredde på svært brede skjermer (datatette rapporter får plass). */
export const CONTAINER = 'mx-auto w-full max-w-[1480px] px-4 sm:px-6 lg:px-8';

/** Sider som ikke bruker den globale produksjonsfilterraden (driftssidene har eget filter i URL-en). */
const NO_FILTER_ROUTES = ['/bestillinger', '/min-dag', '/timer', '/vareforbruk', '/prosjekter', '/rapporter', '/avvik/sak/', '/innstillinger'];
/** Unntak: produksjonsrapporten bruker produksjonsfilteret. */
const FILTER_ROUTE_EXCEPTIONS = ['/rapporter/produksjon'];
/** Sider som fyller hele innholdsflaten selv (ingen container/padding) og ruller internt. */
const FULL_BLEED_ROUTES = ['/assistent'];
/** Oversikt er arbeidsstart og viser registertilstand/oppgaver – produksjonsfilteret hører hjemme i Produksjon. */
const WORK_START_ROUTES = ['/oversikt'];

/**
 * Skal produksjonsfilteret (dato/scenario) vises? Det gjelder bare produksjonsdata i valgt periode.
 * Maskinpark/service og HMS/RUH/kvalitet viser registertilstand og manuelle saker – der skjules filteret
 * (beregningene i produksjonsvisningene er uendret; dette styrer kun synlighet).
 */
export function showsProductionFilters(pathname, search = '') {
  const sp = new URLSearchParams(search);
  if (pathname.startsWith('/maskiner') && !pathname.startsWith('/maskiner/') && sp.get('fane') === 'maskinpark') return false;
  if (pathname === '/avvik' && sp.get('fane') === 'hms') return false;
  if (WORK_START_ROUTES.some((p) => pathname === p)) return false;
  if (FILTER_ROUTE_EXCEPTIONS.some((p) => pathname.startsWith(p))) return true;
  return !NO_FILTER_ROUTES.some((p) => pathname.startsWith(p));
}

const SIDEBAR_KEY = 'hr:sidebar-collapsed';

/**
 * Mobil/nettbrett: toppfeltet glir unna når brukeren ruller nedover i innholdet og kommer tilbake ved første rulling
 * oppover (eller nær toppen). Slik dekker ikke menylinjen innholdet på små skjermer, samtidig som menyen alltid er ett
 * «sveip opp» unna. Desktop (lg+) påvirkes ikke – der er toppfeltet alltid synlig.
 */
function useAutoHideTopbar() {
  const [hidden, setHidden] = useState(false);
  useEffect(() => {
    let last = window.scrollY;
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        const y = Math.max(0, window.scrollY);
        const maxY = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
        const delta = y - last;
        if (y < 72 || y > maxY) setHidden(false);
        else if (delta > 8) setHidden(true);
        else if (delta < -8) setHidden(false);
        last = y;
        ticking = false;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  return hidden;
}

export function pageContext(pathname, search = '') {
  if (pathname.startsWith('/innstillinger')) {
    const slug = pathname.split('/')[2];
    const section = slug ? ADMIN_SECTIONS.find((s) => s.id === slug) : null;
    return section ? { group: 'Administrasjon', title: section.label, parent: { label: 'Innstillinger', to: '/innstillinger', search: '' } } : { group: 'Administrasjon', title: 'Innstillinger', parent: null };
  }
  if (pathname.startsWith('/avvik/sak/')) return { group: 'Kvalitet', title: 'Avvikssak', parent: { label: 'HMS / RUH / Kvalitet', to: '/avvik', search: '?fane=hms' } };
  if (pathname.startsWith('/maskiner/')) return { group: 'Drift', title: 'Maskindetalj', parent: { label: 'Maskiner', to: '/maskiner' } };
  if (pathname.startsWith('/avvik/')) return { group: 'Kvalitet', title: 'Avviksdetalj', parent: { label: 'Avvik', to: '/avvik' } };
  if (pathname === '/avvik' && new URLSearchParams(search).get('fane') === 'hms') return { group: 'Kvalitet', title: 'HMS / RUH / Kvalitet', parent: { label: 'Avvik', to: '/avvik', search: '' } };
  if (pathname.startsWith('/prosjekter/')) return { group: 'Registrering', title: 'Prosjekt', parent: { label: 'Prosjekter', to: '/prosjekter' } };
  if (pathname.startsWith('/rapporter/')) {
    const slug = pathname.split('/')[2];
    const title = slug === 'produksjon' ? 'Perioderapport' : DRIFT_REPORTS.find((r) => r.id === slug)?.label ?? 'Rapport';
    return { group: 'Innsikt', title, parent: { label: 'Rapporter', to: '/rapporter' } };
  }
  for (const g of NAV_GROUPS) {
    const item = g.items.find((n) => pathname.startsWith(n.to));
    if (item) return { group: g.label, title: item.label, parent: null };
  }
  return { group: null, title: 'Produksjonsportal', parent: null };
}

export function Mark({ className }) {
  return <BrandLogo variant="mark" decorative className={cn('h-8 w-8', className)} />;
}

function useCollapsed() {
  const [collapsed, setCollapsed] = useState(() => {
    const stored = readJson(window.localStorage, SIDEBAR_KEY, null);
    if (stored === true || stored === false) return stored;
    // Uten lagret valg: smale desktopvinduer (lg, 1024–1279 px) starter med ikonmeny slik at tabellene får plass.
    return typeof window.matchMedia === 'function' && window.matchMedia('(max-width: 1279px)').matches;
  });
  const toggle = useCallback(() => {
    setCollapsed((c) => {
      writeJson(window.localStorage, SIDEBAR_KEY, !c);
      return !c;
    });
  }, []);
  return [collapsed, toggle];
}

/** Ett menypunkt: 40 px høyt, 20 px lucide-ikon, 14 px tekst. Aktiv = rolig hvit «chip» med hairline. I kollapset modus kun ikon med tooltip. */
function SideItem({ item, search, collapsed, onNavigate, testPrefix = 'sidebar', dense = false }) {
  const Icon = item.icon;
  const slug = item.to.slice(1);
  const link = (
    <NavLink
      to={{ pathname: item.to, search }}
      onClick={onNavigate}
      data-testid={`${testPrefix}-item-${slug}`}
      className={({ isActive }) =>
        cn(
          'group relative flex h-10 items-center gap-2.5 rounded-[10px] text-sm font-medium leading-5 text-[var(--hr-text)] transition-[background-color,color,box-shadow] duration-150 hover:bg-white/70 hover:text-[var(--hr-heading)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--hr-focus)]',
          dense && 'hr-sidenav-item',
          collapsed ? 'w-10 justify-center' : 'px-3',
          isActive && 'bg-white text-[var(--hr-heading)] shadow-[var(--hr-shadow-xs)] ring-1 ring-[var(--hr-hairline)] hover:bg-white',
        )
      }
    >
      {({ isActive }) => (
        <>
          <Icon className={cn('h-5 w-5 shrink-0 transition-colors duration-150', item.accent ? 'text-[var(--hr-accent)]' : isActive ? 'text-[var(--hr-navy)]' : 'text-[var(--hr-muted)] group-hover:text-[var(--hr-heading)]')} strokeWidth={isActive ? 2 : 1.75} aria-hidden="true" />
          {!collapsed && <span className="truncate">{item.label}</span>}
          {!collapsed && isActive && <span className="absolute left-0 top-1/2 h-5 w-[3px] -translate-y-1/2 rounded-r-full bg-[var(--hr-accent)]" aria-hidden="true" />}
        </>
      )}
    </NavLink>
  );
  if (!collapsed) return link;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{link}</TooltipTrigger>
      <TooltipContent side="right" sideOffset={10}>
        {item.label}
      </TooltipContent>
    </Tooltip>
  );
}

function NavGroups({ search, collapsed, onNavigate, testPrefix, dense = false }) {
  return (
    <nav aria-label="Hovedmeny" className={cn('flex flex-col gap-5', dense && 'hr-sidenav-groups', collapsed && 'items-center gap-3')} data-testid={`${testPrefix}-nav`}>
      {NAV_GROUPS.map((g, gi) => (
        <div key={g.id} className={cn('flex flex-col gap-0.5', collapsed && 'items-center')} data-testid={`${testPrefix}-group-${g.id}`}>
          {!collapsed && g.label && <div className="mb-1 px-3 text-[12px] font-semibold uppercase leading-4 tracking-[0.08em] text-[var(--hr-muted-2)]">{g.label}</div>}
          {collapsed && gi > 0 && <div className="mb-1.5 h-px w-6 bg-[var(--hr-border)]" aria-hidden="true" />}
          {g.items.map((item) => (
            <SideItem key={item.to} item={item} search={search} collapsed={collapsed} onNavigate={onNavigate} testPrefix={testPrefix} dense={dense} />
          ))}
        </div>
      ))}
    </nav>
  );
}

/** Brukerkort nederst i menyen med profilmeny. */
function UserMenu({ session, role, collapsed, onAbout, onReset, onLogout, align = 'end', testId = 'profile-menu-button' }) {
  const initials = (session?.demoUser ?? 'B')
    .split(' ')
    .map((n) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={cn(
            'flex items-center gap-2.5 rounded-[10px] text-left transition-colors duration-150 hover:bg-white/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--hr-focus)] data-[state=open]:bg-white/70',
            collapsed ? 'h-10 w-10 justify-center' : 'h-12 w-full px-2',
          )}
          data-testid={testId}
          aria-label="Profilmeny"
        >
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[var(--hr-navy)] text-[12px] font-semibold text-white" aria-hidden="true">
            {initials}
          </span>
          {!collapsed && (
            <>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-semibold leading-5 text-[var(--hr-heading)]" data-testid="profile-menu-user">
                  {session?.demoUser}
                </span>
                <span className="block truncate text-[13px] leading-[18px] text-[var(--hr-muted)]">{session?.title ?? roleLabel(role)}</span>
              </span>
              <MoreHorizontal className="h-4 w-4 shrink-0 text-[var(--hr-muted-2)]" aria-hidden="true" />
            </>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent side={collapsed ? 'right' : 'top'} align={align} sideOffset={8} className="w-64 rounded-[12px] p-1.5 shadow-[var(--hr-shadow-md)]">
        <DropdownMenuLabel className="px-2 py-2 font-normal">
          <div className="text-sm font-medium text-[var(--hr-heading)]">{session?.demoUser}</div>
          <div className="text-[13px] text-[var(--hr-muted)]">{session?.title ? `${session.title} · ${roleLabel(role)}` : roleLabel(role)}</div>
          <div className="hr-tabular mt-1 text-[12px] leading-4 text-[var(--hr-muted-2)]" data-testid="profile-menu-reference-clock">Referansetid {formatDateTime(DEMO_NOW_UTC)}</div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem className="rounded-[8px] py-2" onSelect={onAbout} data-testid="about-portal-menu-item">
          <Info className="mr-2 h-4 w-4 text-[var(--hr-muted)]" aria-hidden="true" /> Om portalen
        </DropdownMenuItem>
        <DropdownMenuItem className="rounded-[8px] py-2" onSelect={onReset} data-testid="reset-demo-open-button">
          <RotateCcw className="mr-2 h-4 w-4 text-[var(--hr-muted)]" aria-hidden="true" /> Nullstill portaldata
        </DropdownMenuItem>
        <DropdownMenuItem className="rounded-[8px] py-2" asChild>
          <Link to="/" data-testid="to-website-menu-item">
            <ExternalLink className="mr-2 h-4 w-4 text-[var(--hr-muted)]" aria-hidden="true" /> Til nettsiden
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem className="rounded-[8px] py-2" onSelect={onLogout} data-testid="logout-button">
          <LogOut className="mr-2 h-4 w-4 text-[var(--hr-muted)]" aria-hidden="true" /> Avslutt demo
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** Brødsmule i toppfeltet: gruppe › side (› detalj). Sidens egen H1 ligger i innholdet. */
function Crumbs({ ctx, search, className }) {
  return (
    <nav aria-label="Du er her" className={cn('flex min-w-0 items-center gap-1.5 text-[13px] leading-[18px]', className)} data-testid="topbar-crumbs">
      {ctx.group && (
        <>
          <span className="text-[var(--hr-muted-2)]">{ctx.group}</span>
          <ChevronRight className="h-3.5 w-3.5 text-[var(--hr-muted-2)]" aria-hidden="true" />
        </>
      )}
      {ctx.parent && (
        <>
          <Link to={{ pathname: ctx.parent.to, search: ctx.parent.search ?? search }} className="rounded-sm text-[var(--hr-muted)] transition-colors duration-150 hover:text-[var(--hr-heading)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--hr-focus)]">
            {ctx.parent.label}
          </Link>
          <ChevronRight className="h-3.5 w-3.5 text-[var(--hr-muted-2)]" aria-hidden="true" />
        </>
      )}
      <span className="truncate text-sm font-semibold text-[var(--hr-heading)]" aria-current="page" data-testid="topbar-title">
        {ctx.title}
      </span>
    </nav>
  );
}

export function AppShell() {
  const { session, role, logout } = useSession();
  const { resetLocalData } = useData();
  const store = useDemoStore();
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, toggleCollapsed] = useCollapsed();
  const topbarHidden = useAutoHideTopbar();
  const [menuOpen, setMenuOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const search = location.search;
  const showFilters = showsProductionFilters(location.pathname, location.search);
  const fullBleed = FULL_BLEED_ROUTES.some((p) => location.pathname.startsWith(p));
  const ctx = pageContext(location.pathname, location.search);

  useEffect(() => {
    document.title = `${ctx.title} · Hardanger Rock`;
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [location.pathname, ctx.title]);

  const onLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  const onReset = () => {
    clearDemoStorage({ includeSession: false });
    store.clear();
    resetLocalData();
    setResetOpen(false);
    toast.success('Portaldata er nullstilt', { description: 'Kommentarer, avviksstatus, tiltaksforslag, importerte filer og endrede satser er fjernet.' });
    navigate('/oversikt');
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success('Lenke kopiert', { description: 'Visningen med valgt periode og filtre kan deles.' });
    } catch {
      toast.error('Kunne ikke kopiere lenken', { description: 'Kopier adressen fra adressefeltet i stedet.' });
    }
  };

  const collapseButton = (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button variant="ghost" size="icon" className="h-9 w-9 rounded-[8px] text-[var(--hr-muted)] hover:bg-white/70 hover:text-[var(--hr-heading)]" onClick={toggleCollapsed} aria-label={collapsed ? 'Utvid menyen' : 'Minimer menyen'} data-testid={collapsed ? 'sidebar-expand-button' : 'sidebar-collapse-button'}>
          {collapsed ? <PanelLeftOpen className="h-[18px] w-[18px]" aria-hidden="true" /> : <PanelLeftClose className="h-[18px] w-[18px]" aria-hidden="true" />}
        </Button>
      </TooltipTrigger>
      <TooltipContent side="right">{collapsed ? 'Utvid menyen' : 'Minimer menyen'}</TooltipContent>
    </Tooltip>
  );

  return (
    <div className="flex min-h-screen bg-white text-[var(--hr-text)]">
      <a href="#main" className="sr-only focus:not-sr-only focus:fixed focus:left-3 focus:top-3 focus:z-[400] focus:rounded-md focus:bg-white focus:px-3 focus:py-2 focus:text-sm focus:shadow-[var(--hr-shadow-md)]">
        Hopp til innhold
      </a>

      {/* Venstre sidemeny (desktop): sticky i full høyde, dempet flate – innholdet er hvitt. */}
      <aside
        className={cn('sticky top-0 hidden h-screen shrink-0 flex-col border-r border-[var(--hr-hairline)] bg-[var(--hr-bg)] transition-[width] duration-200 [transition-timing-function:var(--hr-ease)] lg:flex print:hidden', collapsed ? 'w-16' : 'w-64')}
        data-testid="sidebar"
        data-collapsed={collapsed ? 'true' : 'false'}
      >
        <div className={cn('flex h-14 shrink-0 items-center', collapsed ? 'justify-center' : 'justify-between pl-4 pr-2')}>
          <Link to={{ pathname: '/oversikt', search }} className="flex items-center rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--hr-focus)] focus-visible:ring-offset-2" aria-label="Hardanger Rock – til oversikt" data-testid="sidebar-logo-link">
            {collapsed ? <Mark className="h-7 w-7" /> : <BrandLogo variant="dark" className="h-[26px] w-auto" testId="sidebar-logo" />}
          </Link>
          {!collapsed && collapseButton}
        </div>

        <div className="hr-scroll hr-sidenav-scroll flex-1 overflow-y-auto px-3 py-3">
          <NavGroups search={search} collapsed={collapsed} dense />
        </div>

        <div className={cn('shrink-0 border-t border-[var(--hr-hairline)] px-3 py-3', collapsed && 'flex flex-col items-center gap-2')}>
          <SideItem item={SETTINGS_ITEM} search="" collapsed={collapsed} dense />
          {!collapsed && (
            <div className="hr-tabular hr-sidenav-clock mt-2 flex items-center gap-1.5 px-3 text-[12px] leading-4 text-[var(--hr-muted-2)]" data-testid="reference-clock">
              <Clock3 className="h-3.5 w-3.5" aria-hidden="true" />
              Referansetid {formatDateTime(DEMO_NOW_UTC)}
            </div>
          )}
          <div className={cn('mt-2', collapsed && 'flex flex-col items-center gap-2')}>
            <UserMenu session={session} role={role} collapsed={collapsed} onAbout={() => setAboutOpen(true)} onReset={() => setResetOpen(true)} onLogout={onLogout} align={collapsed ? 'end' : 'start'} />
            {collapsed && collapseButton}
          </div>
        </div>
      </aside>

      {/* Innholdskolonne */}
      <div className="flex min-w-0 flex-1 flex-col">
        <div
          className={cn(
            'sticky top-0 z-[var(--z-topbar)] transition-transform duration-200 [transition-timing-function:var(--hr-ease)] motion-reduce:transition-none print:hidden',
            topbarHidden && !menuOpen && 'max-lg:-translate-y-full',
          )}
          data-testid="sticky-stack"
          data-hidden={topbarHidden ? 'true' : 'false'}
        >
          {/* Desktop: ett toppfelt med brødsmule til venstre og alle filtre til høyre */}
          <header className="hidden border-b border-[var(--hr-hairline)] bg-white/90 backdrop-blur-md supports-[backdrop-filter]:bg-white/85 lg:block" data-testid="topbar">
            {/*
              lg (1024–1279): to rolige rader – brødsmule + del øverst, filtre under.
              xl+: alt på én rad. Styres med order/basis, så alle kontroller finnes alltid.
            */}
            <div className={cn(CONTAINER, 'flex min-h-[56px] flex-wrap items-center gap-x-4 gap-y-2 py-2')}>
              <Crumbs ctx={ctx} search={search} className="order-1 xl:order-1" />
              <div className="order-2 ml-auto flex shrink-0 items-center xl:order-3 xl:ml-0">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="outline" size="sm" className="gap-1.5 px-3" onClick={copyLink} aria-label="Kopier lenke til visningen" data-testid="copy-view-link-button">
                      <Link2 className="h-4 w-4 text-[var(--hr-muted-2)]" aria-hidden="true" />
                      Del
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">Kopier lenke til visningen (periode og filtre følger med)</TooltipContent>
                </Tooltip>
              </div>
              {showFilters && (
                <div className="order-3 basis-full xl:order-2 xl:ml-auto xl:basis-auto">
                  <FilterToolbar />
                </div>
              )}
            </div>
          </header>

          {/* Mobil/nettbrett: toppfelt med meny i skuff */}
          <header className="flex h-14 items-center gap-2 border-b border-[var(--hr-hairline)] bg-white/90 px-3 backdrop-blur-md lg:hidden" data-testid="mobile-topbar">
            <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
              <Button variant="ghost" size="icon" className="h-11 w-11 rounded-[10px]" onClick={() => setMenuOpen(true)} aria-label="Åpne meny" data-testid="mobile-menu-button">
                <Menu className="h-5 w-5" aria-hidden="true" />
              </Button>
              <SheetContent side="left" className="flex w-[320px] max-w-[92vw] flex-col border-r-0 bg-[var(--hr-bg)] p-0" data-testid="mobile-menu-sheet">
                <SheetHeader className="sr-only">
                  <SheetTitle>Meny</SheetTitle>
                </SheetHeader>
                <div className="flex h-14 items-center px-4">
                  <BrandLogo variant="dark" className="h-[26px] w-auto" testId="mobile-nav-logo" />
                </div>
                <div className="hr-scroll flex-1 overflow-y-auto px-3 py-3">
                  <NavGroups search={search} collapsed={false} onNavigate={() => setMenuOpen(false)} testPrefix="mobile-nav" />
                </div>
                <div className="border-t border-[var(--hr-hairline)] px-3 py-3">
                  <SideItem item={SETTINGS_ITEM} search="" collapsed={false} onNavigate={() => setMenuOpen(false)} testPrefix="mobile-nav" />
                  <div className="mt-1">
                    <UserMenu session={session} role={role} collapsed={false} onAbout={() => { setMenuOpen(false); setAboutOpen(true); }} onReset={() => { setMenuOpen(false); setResetOpen(true); }} onLogout={onLogout} align="start" testId="mobile-profile-menu-button" />
                  </div>
                </div>
              </SheetContent>
            </Sheet>
            <Link to={{ pathname: '/oversikt', search }} className="flex items-center rounded-md" aria-label="Hardanger Rock – til oversikt" data-testid="topbar-logo-link">
              <BrandLogo variant="dark" className="h-[26px] w-auto" testId="topbar-logo" />
            </Link>
            <div className="ml-auto flex min-w-0 items-center gap-1">
              <span className="max-w-[40vw] truncate text-sm font-semibold text-[var(--hr-heading)]">{ctx.title}</span>
            </div>
          </header>
          {showFilters && <FilterMobileBar />}
        </div>

        <main className={cn(fullBleed ? 'flex min-h-0 flex-1 flex-col' : cn(CONTAINER, 'py-6'), 'flex-1')} id="main" tabIndex={-1}>
          <div className={cn('hr-enter', fullBleed && 'flex min-h-0 flex-1 flex-col')} key={location.pathname}>
            <Outlet />
          </div>
        </main>
      </div>

      <AboutDialog open={aboutOpen} onOpenChange={setAboutOpen} />
      <AlertDialog open={resetOpen} onOpenChange={setResetOpen}>
        <AlertDialogContent data-testid="reset-demo-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Nullstille portaldata?</AlertDialogTitle>
            <AlertDialogDescription>Fjerner kommentarer, avviksstatus, tiltaksforslag, importerte filer, endrede satser og valgt adapter. Grunnlagsdataene gjenopprettes. Du forblir innlogget.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="reset-demo-cancel-button">Avbryt</AlertDialogCancel>
            <AlertDialogAction onClick={onReset} data-testid="reset-demo-confirm-button">
              Nullstill
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
